<template>
  <el-dialog title="监测条目" :visible.sync="visible" width="760px" append-to-body @open="onOpen">
    <el-form :model="form" label-width="120px" size="small">
      <el-row v-if="isRoutine && siteOptions.length" :gutter="16" class="site-pick-block">
        <el-col :span="24">
          <el-form-item label="从点位库">
            <div class="site-pick-toolbar">
              <el-input
                v-model="siteKw"
                clearable
                size="small"
                placeholder="搜索点位或监测项目"
                prefix-icon="el-icon-search"
                class="site-pick-search"
              />
              <span class="site-pick-count">共 {{ filteredSiteOptions.length }} 条</span>
            </div>
            <el-table
              ref="siteTable"
              :data="filteredSiteOptions"
              size="mini"
              max-height="220"
              highlight-current-row
              class="site-pick-table"
              empty-text="无匹配点位"
              :row-class-name="siteRowClass"
              @row-click="onSiteRowClick"
            >
              <el-table-column prop="site_name" label="监测点位" min-width="148" show-overflow-tooltip />
              <el-table-column label="频次" width="88" align="center">
                <template slot-scope="{ row }">
                  <el-tag v-if="siteFreqLabel(row)" size="mini" type="info" effect="plain">{{ siteFreqLabel(row) }}</el-tag>
                  <span v-else class="muted">—</span>
                </template>
              </el-table-column>
              <el-table-column label="类型" width="108" show-overflow-tooltip>
                <template slot-scope="{ row }">
                  <span v-if="row.sub_type" class="type-cell">{{ row.sub_type }}</span>
                  <span v-else class="muted">—</span>
                </template>
              </el-table-column>
              <el-table-column prop="item_names" label="监测项目" min-width="180" show-overflow-tooltip />
            </el-table>
            <div v-if="pickedSiteId" class="site-pick-selected">
              <span class="site-pick-selected-label">已选</span>
              <strong>{{ pickedSiteSummary }}</strong>
              <el-button type="text" size="mini" @click="clearPickedSite">清除</el-button>
            </div>
            <div v-else class="site-pick-hint">点击表格行带入点位、类型、频次与监测项目</div>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="基本类型" required>
            <el-select v-model="form.basic_type" filterable style="width:100%" @change="onBasicChange">
              <el-option v-for="b in basicTypes" :key="b.label" :label="b.label" :value="b.label" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col v-if="!isRoutine" :span="12">
          <el-form-item label="详细类型" required>
            <el-select v-model="form.sub_type" filterable style="width:100%" @change="onSubChange">
              <el-option v-for="s in subTypes" :key="s.label" :label="s.label" :value="s.label" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item label="监测点位">
        <el-input v-model="form.monitor_place" placeholder="可手输或从企业点位库选择" />
      </el-form-item>
      <el-row :gutter="16">
        <el-col :span="8">
          <el-form-item :label="isRoutine ? '点位数量' : '点口数量'"><el-input-number v-model="form.place_count" :min="1" :max="999" style="width:100%" /></el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="监测天数"><el-input-number v-model="form.total_days" :min="1" :max="365" style="width:100%" /></el-form-item>
        </el-col>
        <el-col v-if="!isRoutine" :span="8">
          <el-form-item label="监测频率"><el-input-number v-model="form.freq_per_day" :min="1" :max="99" style="width:100%" /><span class="unit-hint">次/天</span></el-form-item>
        </el-col>
        <template v-if="isRoutine">
          <el-col :span="8">
            <el-form-item label="监测频次" required>
              <el-select
                v-model="form.freq_label"
                style="width:100%"
                placeholder="请选择：季测、月测等"
                @change="onFreqPresetChange"
              >
                <el-option
                  v-for="(p, k) in freqPresets"
                  :key="k"
                  :label="presetOptionLabel(k, p)"
                  :value="k"
                />
                <el-option label="自定义（手动填次/期）" value="__custom__" />
              </el-select>
              <div v-if="freqPresetHint" class="field-hint">{{ freqPresetHint }}</div>
            </el-form-item>
          </el-col>
        </template>
      </el-row>
      <el-row v-if="isRoutine && isCustomFreq" :gutter="16">
        <el-col :span="24">
          <div class="custom-freq-hint">仅在预设里没有的频次时使用，需手动填写次/期并勾选 12 月排期</div>
        </el-col>
        <el-col :span="8">
          <el-form-item label="次/期"><el-input-number v-model="form.freq_count" :min="1" :max="99" style="width:100%" /></el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="共 N 期"><el-input-number v-model="form.periods_count" :min="1" :max="366" style="width:100%" /></el-form-item>
        </el-col>
      </el-row>
      <el-form-item v-if="isRoutine" label="12月排期">
        <div class="month-grid">
          <el-checkbox v-for="m in 12" :key="m" v-model="schedule[String(m)]" :true-label="1" :false-label="0">{{ m }}月</el-checkbox>
        </div>
      </el-form-item>
      <el-form-item label="监测项目">
        <el-input v-model="form.item_names" placeholder="多个用顿号分隔，或从 CMA 选择" />
        <el-button type="text" size="mini" @click="pickCma">从 CMA 能力选择</el-button>
      </el-form-item>
      <el-form-item label="方法依据">
        <el-input v-model="methodText" placeholder="可选" />
      </el-form-item>
      <el-form-item label="计费标准">
        <el-select v-model="chargeMode" style="width:100%">
          <el-option label="按监测频次计费" value="freq" />
          <el-option label="单项计费" value="single" />
          <el-option label="套餐价格" value="package" />
        </el-select>
      </el-form-item>
      <el-form-item label="备注"><el-input v-model="form.remark" type="textarea" :rows="2" /></el-form-item>
    </el-form>

    <el-dialog title="CMA 监测项目" :visible.sync="showCma" width="600px" append-to-body>
      <el-input v-model="cmaKw" placeholder="搜索项目" clearable style="margin-bottom:8px" @keyup.enter.native="loadCma" />
      <el-table :data="cmaRows" size="mini" max-height="280" @selection-change="onCmaSel">
        <el-table-column type="selection" width="45" />
        <el-table-column prop="param_name" label="项目" />
        <el-table-column prop="std_name" label="方法" min-width="160" />
      </el-table>
      <div slot="footer">
        <el-button @click="showCma=false">取消</el-button>
        <el-button type="primary" @click="applyCma">确定</el-button>
      </div>
    </el-dialog>

    <div slot="footer">
      <el-button @click="visible=false">取消</el-button>
      <el-button type="primary" @click="save">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import api from '@/utils/api'
import { resolveRoutinePresetKey, inferRoutinePresetKey, normalizeFormFreqValue, routinePresetOptionLabel, parseRoutineSchedule, scheduleHasMonth as routineScheduleHasMonth } from '@/utils/routineFreq'
import { looksLikeItemNames, normalizeMonitorItemRow, siteToMonitorItem } from '@/utils/siteMonitorMap'
import { ensureDefaultSubType } from '@/utils/routineTypeInfer'

const emptyForm = () => ({
  basic_type: '',
  sub_type: '',
  type_code: '',
  monitor_place: '',
  place_count: 1,
  total_days: 1,
  freq_per_day: 1,
  freq_unit: 'day',
  freq_count: 1,
  periods_count: 1,
  freq_label: '',
  schedule_json: '',
  item_names: '',
  item_ids: '',
  method_accord_json: '',
  charge_by_frequency: 1,
  remark: '',
  site_id: null
})

const emptySchedule = () => {
  const s = {}
  for (let m = 1; m <= 12; m++) s[String(m)] = 0
  return s
}

export default {
  props: {
    value: { type: Boolean, default: false },
    row: { type: Object, default: null },
    typeConfig: { type: Object, default: () => ({}) },
    flowModel: { type: String, default: 'SimplifyMonitorEntrust' },
    entId: { type: [Number, String], default: null },
    freqPresetConfig: { type: Object, default: () => ({}) },
    typeInferConfig: { type: Object, default: () => ({}) },
    siteOptions: { type: Array, default: () => [] }
  },
  data() {
    return {
      form: emptyForm(),
      schedule: emptySchedule(),
      chargeMode: 'freq',
      methodText: '',
      showCma: false,
      cmaKw: '',
      cmaRows: [],
      cmaSelected: [],
      pickedSiteId: null,
      siteKw: ''
    }
  },
  computed: {
    visible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) }
    },
    isRoutine() {
      return this.flowModel === 'RoutineEntrustFlow'
    },
    basicTypes() {
      return (this.typeConfig && this.typeConfig.basicTypes) || []
    },
    subTypes() {
      const b = this.basicTypes.find(x => x.label === this.form.basic_type)
      return b ? (b.subTypes || []) : []
    },
    freqPresets() {
      return (this.freqPresetConfig && this.freqPresetConfig.presets) || {}
    },
    filteredSiteOptions() {
      const kw = (this.siteKw || '').trim().toLowerCase()
      if (!kw) return this.siteOptions
      return this.siteOptions.filter(s => {
        const hay = `${s.site_name || ''} ${s.freq_label || ''} ${s.sub_type || ''} ${s.item_names || ''}`.toLowerCase()
        return hay.includes(kw)
      })
    },
    pickedSiteName() {
      const s = this.siteOptions.find(x => x.id === this.pickedSiteId)
      return s ? (s.site_name || '') : ''
    },
    pickedSiteSummary() {
      const s = this.siteOptions.find(x => x.id === this.pickedSiteId)
      if (!s) return ''
      const freq = this.siteFreqLabel(s)
      const parts = [s.site_name, freq].filter(Boolean)
      return parts.join(' · ')
    },
    isCustomFreq() {
      return this.isRoutine && this.form.freq_label === '__custom__'
    },
    freqPresetHint() {
      if (!this.isRoutine) return ''
      const label = this.form.freq_label
      if (!label || label === '__custom__') return ''
      const key = resolveRoutinePresetKey(label, this.freqPresets)
      const p = key ? this.freqPresets[key] : null
      if (!p) return ''
      return `已自动设置：${p.freq_count || 1}次/期，共${p.periods_count || 1}期，并勾选对应月份`
    }
  },
  watch: {
    siteOptions() {
      this.syncSiteTableCurrent()
    },
    pickedSiteId() {
      this.syncSiteTableCurrent()
    },
    'form.freq_label'(val) {
      if (!this.isRoutine || !val || val === '__custom__') return
      this.syncFormFreqLabel()
      const key = resolveRoutinePresetKey(this.form.freq_label, this.freqPresets)
      if (key && this.freqPresets[key]) {
        this.$nextTick(() => this.applyPreset(key))
      }
    }
  },
  methods: {
    siteRowClass({ row }) {
      return row && row.id === this.pickedSiteId ? 'site-pick-row-active' : ''
    },
    siteFreqLabel(row) {
      if (!row) return ''
      const raw = String(row.freq_label || '').trim()
      const key = inferRoutinePresetKey(row, this.freqPresets)
      if (key) return key
      if (raw === '自定义') return '自定义'
      if (raw) return raw
      return ''
    },
    syncSiteTableCurrent() {
      this.$nextTick(() => {
        const tbl = this.$refs.siteTable
        if (!tbl) return
        const row = this.siteOptions.find(x => x.id === this.pickedSiteId)
        tbl.setCurrentRow(row || null)
      })
    },
    onSiteRowClick(row) {
      if (!row || !row.id) return
      this.pickedSiteId = row.id
      this.applySite(row.id)
      this.syncSiteTableCurrent()
    },
    clearPickedSite() {
      this.pickedSiteId = null
      this.siteKw = ''
      this.syncSiteTableCurrent()
    },
    parseSchedule(raw) {
      return parseRoutineSchedule(raw)
    },
    scheduleHasMonth(sch) {
      return routineScheduleHasMonth(sch)
    },
    applySchedule(sch) {
      const parsed = this.parseSchedule(sch)
      for (let m = 1; m <= 12; m++) {
        const k = String(m)
        this.$set(this.schedule, k, parsed[k] ? 1 : 0)
      }
    },
    onOpen() {
      this.pickedSiteId = null
      this.siteKw = ''
      if (this.row) {
        this.form = { ...emptyForm(), ...this.row }
        if (this.form.site_id) this.pickedSiteId = this.form.site_id
        if (this.isRoutine) {
          this.form.freq_unit = 'period'
          if (!this.form.freq_count) this.form.freq_count = this.form.freq_per_day || 1
          this.schedule = this.parseSchedule(this.form.schedule_json)
          this.syncRoutineFreqFromRow()
          this.normalizeItemFields()
        } else {
          this.schedule = this.parseSchedule(this.form.schedule_json)
        }
        this.chargeMode = this.form.charge_by_frequency ? 'freq' : 'single'
        try {
          const m = JSON.parse(this.form.method_accord_json || '[]')
          this.methodText = Array.isArray(m) ? m.join('；') : String(this.form.method_accord_json || '')
        } catch (e) {
          this.methodText = String(this.form.method_accord_json || '')
        }
        ensureDefaultSubType(this.form, this.typeConfig)
      } else {
        this.form = emptyForm()
        if (this.isRoutine) {
          this.form.freq_unit = 'period'
          this.form.freq_count = 1
          this.form.periods_count = 1
        }
        this.schedule = emptySchedule()
        this.chargeMode = 'freq'
        this.methodText = ''
      }
      this.syncSiteTableCurrent()
    },
    applySite(siteId) {
      const s = this.siteOptions.find(x => x.id === siteId)
      if (!s) return
      const item = siteToMonitorItem(s, {
        typeInferConfig: this.typeInferConfig,
        typeConfig: this.typeConfig,
        freqPresets: this.freqPresets,
        isRoutine: this.isRoutine
      })
      Object.assign(this.form, item)
      if (this.isRoutine && item.schedule_json) {
        this.applySchedule(item.schedule_json)
      }
      this.normalizeItemFields()
    },
    normalizeItemFields() {
      const fixed = normalizeMonitorItemRow(this.form)
      this.form.monitor_place = fixed.monitor_place
      this.form.item_names = fixed.item_names
    },
    syncFormFreqLabel() {
      if (!this.isRoutine) return
      const label = (this.form.freq_label || '').trim()
      if (!label || label === '__custom__') return
      const key = resolveRoutinePresetKey(label, this.freqPresets)
      if (key && key !== label && this.freqPresets[key]) {
        this.form.freq_label = key
      }
    },
    presetOptionLabel(key, preset) {
      return routinePresetOptionLabel(key, preset)
    },
    syncRoutineFreqFromRow() {
      if (!this.isRoutine) return
      const presetKey = inferRoutinePresetKey({
        freq_label: this.form.freq_label,
        freq_count: this.form.freq_count,
        periods_count: this.form.periods_count,
        schedule_json: this.schedule
      }, this.freqPresets)
      if (presetKey && this.freqPresets[presetKey]) {
        this.form.freq_label = presetKey
        if (!this.scheduleHasMonth(this.schedule)) {
          this.applyPreset(presetKey)
        }
        return
      }
      this.form.freq_label = normalizeFormFreqValue(this.form.freq_label, this.freqPresets)
      if (!this.form.freq_label && (this.scheduleHasMonth(this.schedule)
        || (this.form.freq_count || 1) > 1 || (this.form.periods_count || 1) > 1)) {
        this.form.freq_label = '__custom__'
      }
    },
    resolveRoutineFreqOnEdit() {
      this.syncRoutineFreqFromRow()
    },
    onFreqPresetChange(label) {
      if (!label || label === '__custom__') return
      const key = resolveRoutinePresetKey(label, this.freqPresets)
      if (key && key !== label) this.form.freq_label = key
      this.applyPreset(key || label)
    },
    applyPreset(label) {
      const key = resolveRoutinePresetKey(label, this.freqPresets)
      if (!key || !this.freqPresets[key]) return
      const p = this.freqPresets[key]
      if (p.freq_count) this.form.freq_count = p.freq_count
      if (p.periods_count) this.form.periods_count = p.periods_count
      if (p.schedule) this.applySchedule(p.schedule)
    },
    onBasicChange() {
      this.form.sub_type = ''
      this.form.type_code = ''
    },
    onSubChange() {
      const s = this.subTypes.find(x => x.label === this.form.sub_type)
      this.form.type_code = s ? (s.code || '') : ''
    },
    pickCma() {
      this.showCma = true
      this.loadCma()
    },
    async loadCma() {
      const res = await api.get('yn_cma_scope_list', { keyword: this.cmaKw, page_size: 50 })
      this.cmaRows = (res.data && res.data.items) || []
    },
    onCmaSel(rows) { this.cmaSelected = rows || [] },
    applyCma() {
      const names = this.cmaSelected.map(r => r.param_name).filter(Boolean)
      const methods = this.cmaSelected.map(r => r.std_name || r.std_code).filter(Boolean)
      this.form.item_names = names.join('、')
      this.methodText = methods.join('；')
      this.showCma = false
    },
    save() {
      if (!this.form.basic_type) {
        this.$message.warning('请选择基本类型')
        return
      }
      if (!this.isRoutine && !this.form.sub_type) {
        this.$message.warning('请选择详细类型')
        return
      }
      if (this.isRoutine && !this.form.monitor_place) {
        this.$message.warning('请填写监测点位')
        return
      }
      if (this.isRoutine && !this.form.freq_label) {
        this.$message.warning('请选择监测频次')
        return
      }
      this.form.charge_by_frequency = this.chargeMode === 'freq' ? 1 : 0
      this.form.method_accord_json = this.methodText
      this.normalizeItemFields()
      if (this.isRoutine) {
        this.form.freq_unit = 'period'
        this.form.freq_per_day = this.form.freq_count || 1
        if (this.form.freq_label === '__custom__') {
          this.form.freq_label = ''
        }
        const hasMonth = Object.keys(this.schedule).some(k => this.schedule[k])
        if (!hasMonth && this.form.freq_label && this.freqPresets[this.form.freq_label]) {
          this.applyPreset(this.form.freq_label)
        }
        this.form.schedule_json = JSON.stringify(this.schedule)
      } else {
        this.form.freq_unit = 'day'
        this.form.freq_count = this.form.freq_per_day || 1
      }
      this.$emit('save', normalizeMonitorItemRow({ ...this.form }))
      this.visible = false
    }
  }
}
</script>

<style scoped>
.unit-hint { margin-left: 8px; color: #909399; font-size: 12px; }
.field-hint { margin-top: 4px; color: #67c23a; font-size: 12px; line-height: 1.4; }
.custom-freq-hint { margin-bottom: 8px; font-size: 12px; color: #909399; }
.month-grid { display: flex; flex-wrap: wrap; gap: 8px 16px; }
.site-pick-block { margin-bottom: 4px; }
.site-pick-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.site-pick-search { flex: 1; }
.site-pick-count { flex-shrink: 0; font-size: 12px; color: #909399; }
.site-pick-table { width: 100%; border: 1px solid #e4e7ed; border-radius: 6px; overflow: hidden; }
.site-pick-table >>> .el-table__header th { background: #f5f7fa; color: #606266; font-weight: 500; }
.site-pick-table >>> .el-table__row { cursor: pointer; }
.site-pick-table >>> .el-table__body tr.current-row > td,
.site-pick-table >>> .el-table__body tr.site-pick-row-active > td { background: #ecf5ff !important; }
.site-pick-table >>> .el-table__body tr.site-pick-row-active > td:first-child { box-shadow: inset 3px 0 0 #409eff; }
.type-cell { font-size: 12px; color: #606266; }
.site-pick-hint { margin-top: 8px; font-size: 12px; color: #909399; line-height: 1.5; }
.site-pick-selected {
  margin-top: 8px; padding: 6px 10px; font-size: 12px; color: #606266;
  background: #f0f9eb; border: 1px solid #e1f3d8; border-radius: 4px;
}
.site-pick-selected-label { color: #67c23a; margin-right: 6px; }
.site-pick-selected strong { color: #303133; font-weight: 500; }
.muted { color: #c0c4cc; }
</style>
